import { api } from '@/lib/api/client'
import type { Contract, SlaPolicy, Ticket, ContractStatus } from '@/types/support'
import { fetchAll } from '@/lib/api/paged'

interface Collection<T> {
  data: T[]
}

export async function fetchTickets(params: Record<string, string | number | boolean> = {}): Promise<Ticket[]> {
  return fetchAll<Ticket>('/tickets', params)
}

export async function fetchTicket(key: string): Promise<Ticket> {
  const { data } = await api.get<{ data: Ticket }>(`/tickets/${key}`)

  return data.data
}

export async function fetchContracts(): Promise<Contract[]> {
  const { data } = await api.get<Collection<Contract>>('/contracts')

  return data.data
}

export async function fetchSlaPolicies(): Promise<SlaPolicy[]> {
  const { data } = await api.get<Collection<SlaPolicy>>('/sla-policies')

  return data.data
}

export async function openTicket(input: {
  projectId: string
  title: string
  description?: string
  priority?: string
}): Promise<Ticket> {
  const { data } = await api.post<{ data: Ticket }>('/tickets', input)

  return data.data
}

/** Turn a client's report into a tracked bug on the delivery board (FR-S6). */
export async function promoteTicket(key: string): Promise<Ticket> {
  const { data } = await api.post<{ data: Ticket }>(`/tickets/${key}/promote`, {})

  return data.data
}

/**
 * Recompute the SLA clock from the ticket's own history.
 *
 * Needed after a contract or policy changes: the promise a clock was measuring
 * is not the promise that now applies.
 */
export async function recomputeTicketClock(key: string): Promise<Ticket> {
  const { data } = await api.post<{ data: Ticket }>(`/tickets/${key}/recompute-clock`, {})

  return data.data
}

export interface ContractChanges {
  name?: string
  reference?: string | null
  startsOn?: string
  endsOn?: string | null
  status?: ContractStatus
  notes?: string | null
}

/** The client is fixed at creation — a contract that changes hands is a new contract. */
export async function createContract(input: ContractChanges & { clientId: string }): Promise<Contract> {
  const { data } = await api.post<{ data: Contract }>('/contracts', input)

  return data.data
}

export async function updateContract(id: string, changes: ContractChanges): Promise<Contract> {
  const { data } = await api.patch<{ data: Contract }>(`/contracts/${id}`, changes)

  return data.data
}

export async function deleteContract(id: string): Promise<void> {
  await api.delete(`/contracts/${id}`)
}

export interface SlaPolicyChanges {
  name?: string
  priority?: string
  firstResponseMinutes?: number
  resolutionMinutes?: number
  businessHoursOnly?: boolean
}

export async function createSlaPolicy(
  input: SlaPolicyChanges & { contractId: string | null },
): Promise<SlaPolicy> {
  const { data } = await api.post<{ data: SlaPolicy }>('/sla-policies', input)

  return data.data
}

export async function updateSlaPolicy(id: string, changes: SlaPolicyChanges): Promise<SlaPolicy> {
  const { data } = await api.patch<{ data: SlaPolicy }>(`/sla-policies/${id}`, changes)

  return data.data
}

export async function deleteSlaPolicy(id: string): Promise<void> {
  await api.delete(`/sla-policies/${id}`)
}
